import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';

/**
 * ⛔ s706 (Б-09 аудита) — ПРИБОР ОШИБОК ЗНАЕТ, У КОГО СЛУЧИЛАСЬ ОШИБКА.
 *
 * Замер 05.10.2026: у всех событий панели поле пользователя пустое. Кто из
 * менеджеров получил ошибку, восстанавливали по адресу, браузеру и времени через
 * журнал действий — то есть не из прибора.
 *
 * Сотрудник называется номером учётки, именем и ролью. Почта, телефон и адрес в
 * событие не кладутся (`sendDefaultPii` выключен и остаётся выключенным).
 *
 * ⛔ ОДНО МЕСТО НА ВСЕ ДОРОГИ ВХОДА. Личность приходит в панель многими путями: вход
 * паролем, второй фактор, «войти как», возврат к себе, фоновое обновление прав раз в
 * минуту. Все они кончаются записью в кэше запроса `['auth', 'me']` — подписка стоит
 * на нём, а не в каждой форме входа: новая дорога, написанная завтра, не забудет
 * представиться.
 */

export interface SotrudnikDlyaPribora {
  id: number;
  name: string;
  roles?: string[];
  impersonated_by?: { id: number; name: string } | null;
}

let poslednee = '';

/** Назвать прибору того, кто сейчас в панели; `null` — никто не вошёл. */
export function nazvatSotrudnikaPriboru(user: SotrudnikDlyaPribora | null | undefined): void {
  const rol = user ? [...(user.roles ?? [])].sort().join('+') || 'bez_roli' : '';
  const kto = user?.impersonated_by?.id ?? null;
  const otpechatok = user ? `${user.id}|${user.name}|${rol}|${kto ?? ''}` : '';

  // Кэш личности обновляется раз в минуту — прибор трогаем только при смене.
  if (otpechatok === poslednee) return;
  poslednee = otpechatok;

  if (!user) {
    Sentry.setUser(null);
    Sentry.setTag('rol', undefined);
    Sentry.setTag('voshel_kak', undefined);
    return;
  }

  Sentry.setUser({ id: String(user.id), username: user.name });
  Sentry.setTag('rol', rol);
  // Под «войти как» за экраном сидит другой человек — он назван отдельно.
  Sentry.setTag('voshel_kak', kto !== null ? String(kto) : undefined);
}

/** Подписка на кэш личности: любая смена того, кто вошёл, доезжает до прибора. */
export function sledovatZaSotrudnikom(queryClient: QueryClient): () => void {
  const cache = queryClient.getQueryCache();

  const sverit = (): void => {
    nazvatSotrudnikaPriboru(queryClient.getQueryData<SotrudnikDlyaPribora | null>(['auth', 'me']));
  };

  sverit();

  return cache.subscribe((event) => {
    const klyuch = event.query.queryKey;
    if (klyuch[0] === 'auth' && klyuch[1] === 'me') sverit();
  });
}
